import React, { useState, useEffect } from 'react';
import { Users, Plus, Copy, Check, Edit2, Trash2, X, Link, ChevronDown, ChevronRight, UserPlus, Shield, Settings, Mail, Eye, Banknote, FileText, Download, AlertTriangle, RotateCcw, DollarSign } from 'lucide-react';
import { supabase } from '../supabase';
import { formatCurrency } from '../utils/commissionCalc';
import { logAdminAction } from '../utils/auditLog';

const getAdminEmail = () => {
  try { return JSON.parse(localStorage.getItem('workvan_internal_auth'))?.email; } catch(e) { return null; }
};

// Admins who are ALSO affiliates. Maps their admin email -> their affiliate
// email so we can resolve a parent_affiliate_id default at form-open time.
// Today: only Trent.
const ADMIN_AS_AFFILIATE_EMAIL = {
  'trent@workvanapp.com': 'trenttreadway1@gmail.com',
};

export default function Affiliates({ adminEmail }) {
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [expandedTeams, setExpandedTeams] = useState({});
  const [invitingId, setInvitingId] = useState(null);
  const [terminatingAffiliate, setTerminatingAffiliate] = useState(null);
  const [graceDays, setGraceDays] = useState(90);
  const [detailAffiliate, setDetailAffiliate] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    code: '',
    commission_model: 'fixed',
    commission_rate: '5.00',
    notes: '',
    // Hierarchy fields
    tier: 'affiliate',
    parent_affiliate_id: '',
    can_recruit: false,
    can_set_sub_rates: false,
    override_model: 'fixed',
    override_rate: '5',
    pool_rate: '20',
    director_max_override: '0',
    portal_enabled: false,
    commission_duration_months: '',
    close_bonus_amount: '50',
    enablement_bonus_amount: '0',
    can_grant_deal_bonus: false
  });

  // Base URL for referral links
  const BASE_URL = 'https://workvanapp.com';

  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    try {
      const { data: affiliatesData } = await supabase
        .from('affiliates')
        .select('*')
        .order('name');

      // Get account counts per affiliate
      const { data: companies } = await supabase
        .from('companies')
        .select('referred_by_affiliate_id, subscription_status');

      // Get commission totals
      const { data: commissions } = await supabase
        .from('affiliate_commissions')
        .select('affiliate_id, commission_amount, status');

      const affiliatesWithStats = (affiliatesData || []).map(affiliate => {
        const referredCompanies = companies?.filter(c => c.referred_by_affiliate_id === affiliate.id) || [];
        const accountCount = referredCompanies.length;
        const activeCount = referredCompanies.filter(c => c.subscription_status === 'active').length;

        const affiliateCommissions = commissions?.filter(c => c.affiliate_id === affiliate.id) || [];
        const totalEarned = affiliateCommissions.reduce((sum, c) => sum + parseFloat(c.commission_amount || 0), 0);
        const totalOwed = affiliateCommissions.filter(c => c.status === 'owed').reduce((sum, c) => sum + parseFloat(c.commission_amount || 0), 0);

        // Count sub-affiliates
        const subCount = affiliatesData?.filter(a => a.parent_affiliate_id === affiliate.id).length || 0;

        return {
          ...affiliate,
          accountCount,
          activeCount,
          totalEarned,
          totalOwed,
          subCount
        };
      });

      setAffiliates(affiliatesWithStats);
    } catch (error) {
      console.error('Error loading affiliates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        name: form.name,
        email: form.email || null,
        phone: form.phone || null,
        code: form.code.toLowerCase().replace(/\s+/g, ''),
        commission_model: form.commission_model,
        commission_rate: form.commission_model === 'percentage'
          ? parseFloat(form.commission_rate) / 100
          : parseFloat(form.commission_rate),
        notes: form.notes || null,
        active: true,
        // Hierarchy fields
        tier: form.tier,
        parent_affiliate_id: form.parent_affiliate_id || null,
        can_recruit: form.tier === 'recruiter' || form.tier === 'director',
        director_max_override: form.tier === 'director' ? parseFloat(form.director_max_override) || 0 : 0,
        can_set_sub_rates: form.can_set_sub_rates,
        override_model: form.override_model,
        override_rate: (parseFloat(form.override_rate) || 0) / 100,
        pool_rate: form.override_model === 'from_pool' ? (parseFloat(form.pool_rate) || 0) / 100 : null,
        portal_enabled: form.portal_enabled,
        commission_duration_months: form.commission_duration_months ? parseInt(form.commission_duration_months) : null,
        close_bonus_amount: form.close_bonus_amount ? parseFloat(form.close_bonus_amount) : 0,
        enablement_bonus_amount: form.enablement_bonus_amount ? parseFloat(form.enablement_bonus_amount) : 0,
        can_grant_deal_bonus: form.can_grant_deal_bonus
      };

      if (editingId) {
        await supabase.from('affiliates').update(data).eq('id', editingId);
      } else {
        const { data: newAffiliate, error: insertError } = await supabase
          .from('affiliates')
          .insert([data])
          .select()
          .single();

        if (insertError) throw insertError;

        // Send welcome email if affiliate has email
        // The welcome email will include a password setup link if portal_enabled is true
        if (newAffiliate && data.email) {
          try {
            await fetch('https://workvanapp.com/api/send-affiliate-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'welcome',
                affiliate: {
                  id: newAffiliate.id,
                  name: data.name,
                  email: data.email,
                  code: data.code,
                  commission_model: data.commission_model,
                  commission_rate: data.commission_rate,
                  can_recruit: data.can_recruit,
                  override_model: data.override_model,
                  override_rate: data.override_rate,
                  portal_enabled: data.portal_enabled
                }
              })
            });
          } catch (emailError) {
            console.error('Failed to send welcome email:', emailError);
            // Don't fail the creation if email fails
          }
        }
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadAffiliates();
    } catch (error) {
      alert('Error saving affiliate: ' + error.message);
    }
  };

  const handleEdit = (affiliate) => {
    setForm({
      name: affiliate.name,
      email: affiliate.email || '',
      phone: affiliate.phone || '',
      code: affiliate.code,
      commission_model: affiliate.commission_model,
      commission_rate: affiliate.commission_model === 'percentage'
        ? ((affiliate.commission_rate || 0) * 100).toString()
        : affiliate.commission_rate?.toString() || '5.00',
      notes: affiliate.notes || '',
      tier: affiliate.tier || 'affiliate',
      parent_affiliate_id: affiliate.parent_affiliate_id || '',
      can_recruit: affiliate.can_recruit || false,
      can_set_sub_rates: affiliate.can_set_sub_rates || false,
      override_model: affiliate.override_model || 'fixed',
      override_rate: ((affiliate.override_rate || 0) * 100).toString(),
      pool_rate: ((affiliate.pool_rate || 0) * 100).toString(),
      director_max_override: affiliate.director_max_override?.toString() || '0',
      portal_enabled: affiliate.portal_enabled || false,
      commission_duration_months: affiliate.commission_duration_months?.toString() || '',
      close_bonus_amount: affiliate.close_bonus_amount?.toString() || '0',
      enablement_bonus_amount: affiliate.enablement_bonus_amount?.toString() || '0',
      can_grant_deal_bonus: affiliate.can_grant_deal_bonus || false
    });
    setEditingId(affiliate.id);
    setShowForm(true);
  };

  const handleAddSubAffiliate = (parentAffiliate) => {
    resetForm();
    setForm(prev => ({
      ...prev,
      parent_affiliate_id: parentAffiliate.id,
      commission_rate: parentAffiliate.override_model === 'from_pool'
        ? (parseFloat(parentAffiliate.pool_rate) * 0.7).toFixed(2)
        : '5.00'
    }));
    setEditingId(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    // Check for sub-affiliates
    const subAffiliates = affiliates.filter(a => a.parent_affiliate_id === id);
    if (subAffiliates.length > 0) {
      alert('Cannot delete: this affiliate has sub-affiliates. Reassign or delete them first.');
      return;
    }

    // Confirm deletion
    if (!window.confirm('Delete this affiliate and all related records? This cannot be undone.')) return;

    try {
      // 1. Unlink referred companies (set referred_by_affiliate_id to null)
      const { error: unlinkError } = await supabase
        .from('companies')
        .update({ referred_by_affiliate_id: null })
        .eq('referred_by_affiliate_id', id);
      if (unlinkError) console.warn('Error unlinking companies:', unlinkError);

      // 2. Delete commission records
      const { error: commError } = await supabase
        .from('affiliate_commissions')
        .delete()
        .eq('affiliate_id', id);
      if (commError) console.warn('Error deleting commissions:', commError);

      // 3. Delete affiliate portal users
      const { error: usersError } = await supabase
        .from('affiliate_users')
        .delete()
        .eq('affiliate_id', id);
      if (usersError) console.warn('Error deleting affiliate users:', usersError);

      // 4. Delete override commission settings (as parent or sub)
      const { error: overrideError1 } = await supabase
        .from('affiliate_override_commissions')
        .delete()
        .eq('parent_affiliate_id', id);
      if (overrideError1) console.warn('Error deleting override commissions (parent):', overrideError1);

      const { error: overrideError2 } = await supabase
        .from('affiliate_override_commissions')
        .delete()
        .eq('sub_affiliate_id', id);
      if (overrideError2) console.warn('Error deleting override commissions (sub):', overrideError2);

      // 5. Finally delete the affiliate
      const { error } = await supabase.from('affiliates').delete().eq('id', id);
      if (error) throw error;

      loadAffiliates();
    } catch (error) {
      alert('Error deleting affiliate: ' + error.message);
    }
  };

  const handleToggleActive = async (affiliate) => {
    try {
      await supabase
        .from('affiliates')
        .update({ active: !affiliate.active })
        .eq('id', affiliate.id);
      loadAffiliates();
    } catch (error) {
      alert('Error updating affiliate: ' + error.message);
    }
  };

  const copyReferralLink = (code) => {
    navigator.clipboard.writeText(`${BASE_URL}?ref=${code}`);
    setCopiedId(code);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleTeamExpand = (affiliateId) => {
    setExpandedTeams(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const handleInviteToPortal = async (affiliate) => {
    if (!affiliate.email) {
      alert('Affiliate needs an email address to receive portal invite.');
      return;
    }
    if (!affiliate.portal_enabled) {
      alert('Please enable portal access for this affiliate first.');
      return;
    }

    setInvitingId(affiliate.id);

    try {
      // Send password setup email
      const response = await fetch('https://workvanapp.com/api/send-affiliate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'password_setup',
          affiliate: {
            id: affiliate.id,
            name: affiliate.name,
            email: affiliate.email
          }
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to send invite');
      }

      alert(`Setup email sent to ${affiliate.email}! They'll receive a link to create their password.`);
    } catch (error) {
      console.error('Error sending invite:', error);
      alert('Error sending invite: ' + error.message);
    } finally {
      setInvitingId(null);
    }
  };

  const handleImpersonate = (affiliate) => {
    // Generate a simple impersonation token (base64 encoded affiliate ID + timestamp)
    const timestamp = Date.now();
    const token = btoa(`admin:${affiliate.id}:${timestamp}`);
    const portalUrl = `https://affiliates.workvanapp.com?impersonate=${token}`;
    window.open(portalUrl, '_blank');
  };

  const handleTerminate = async (affiliate) => {
    try {
      const now = new Date();
      const expiresAt = new Date(now.getTime() + graceDays * 86400000);
      const { error } = await supabase
        .from('affiliates')
        .update({
          terminated_at: now.toISOString(),
          access_expires_at: expiresAt.toISOString(),
          active: false
        })
        .eq('id', affiliate.id);
      if (error) throw error;

      await logAdminAction(getAdminEmail(), 'terminate_affiliate', 'affiliate', affiliate.id, affiliate.name, { grace_days: graceDays });
      setTerminatingAffiliate(null);
      setGraceDays(90);
      loadAffiliates();
    } catch (error) {
      alert('Error terminating affiliate: ' + error.message);
    }
  };

  const handleReinstate = async (affiliate) => {
    if (!window.confirm(`Reinstate ${affiliate.name} as an active affiliate?`)) return;
    try {
      const { error } = await supabase
        .from('affiliates')
        .update({
          terminated_at: null,
          access_expires_at: null,
          active: true,
          portal_enabled: true
        })
        .eq('id', affiliate.id);
      if (error) throw error;

      await logAdminAction(getAdminEmail(), 'reinstate_affiliate', 'affiliate', affiliate.id, affiliate.name, {});
      loadAffiliates();
    } catch (error) {
      alert('Error reinstating affiliate: ' + error.message);
    }
  };

  // If the admin viewing the page is also an affiliate (e.g., Trent), the
  // most common case is they're adding their own recruit — so default
  // parent_affiliate_id to themselves. They can still override to "None"
  // in the form. Resolved from the already-loaded affiliates list.
  const myAffiliateEmail = ADMIN_AS_AFFILIATE_EMAIL[(adminEmail || '').toLowerCase()];
  const myAffiliateId = myAffiliateEmail
    ? affiliates.find(a => (a.email || '').toLowerCase() === myAffiliateEmail)?.id || ''
    : '';

  const resetForm = () => {
    setForm({
      name: '',
      email: '',
      phone: '',
      code: '',
      commission_model: 'fixed',
      commission_rate: '5.00',
      notes: '',
      tier: 'affiliate',
      parent_affiliate_id: myAffiliateId,
      can_recruit: false,
      can_set_sub_rates: false,
      override_model: 'fixed',
      override_rate: '0.05',
      pool_rate: '0.20',
      director_max_override: '0',
      portal_enabled: false,
      commission_duration_months: '',
      close_bonus_amount: '25',
      enablement_bonus_amount: '25',
      can_grant_deal_bonus: false
    });
  };

  // Separate top-level affiliates from sub-affiliates
  const topLevelAffiliates = affiliates.filter(a => !a.parent_affiliate_id);
  const getSubAffiliates = (parentId) => affiliates.filter(a => a.parent_affiliate_id === parentId);

  const formatCommissionDisplay = (affiliate) => {
    if (affiliate.commission_model === 'fixed') {
      return `${formatCurrency(affiliate.commission_rate)}/mo`;
    }
    return `${(affiliate.commission_rate * 100).toFixed(0)}%`;
  };

  const formatOverrideDisplay = (affiliate) => {
    if (!affiliate.can_recruit) return null;

    switch (affiliate.override_model) {
      case 'fixed':
        return `+${(affiliate.override_rate * 100).toFixed(1)}% of sale`;
      case 'percentage_of_sub':
        return `+${(affiliate.override_rate * 100).toFixed(0)}% of sub's commission`;
      case 'from_pool':
        return `Pool: ${(affiliate.pool_rate * 100).toFixed(0)}% total`;
      default:
        return null;
    }
  };

  // Get recruiters for parent dropdown
  const availableParents = affiliates.filter(a => a.can_recruit && a.id !== editingId);

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading affiliates...
      </div>
    );
  }

  const AffiliateCard = ({ affiliate, isSubAffiliate = false }) => {
    const subAffiliates = getSubAffiliates(affiliate.id);
    const isExpanded = expandedTeams[affiliate.id];

    return (
      <div style={{ marginLeft: isSubAffiliate ? '2rem' : 0 }}>
        <div
          style={{
            background: isSubAffiliate ? '#151515' : '#1a1a1a',
            borderRadius: '12px',
            border: `1px solid ${isSubAffiliate ? '#252525' : '#2a2a2a'}`,
            padding: '1.25rem',
            opacity: affiliate.active ? 1 : 0.6,
            marginBottom: '0.75rem'
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {/* Expand button for recruiters with subs */}
              {affiliate.can_recruit && subAffiliates.length > 0 && (
                <button
                  onClick={() => toggleTeamExpand(affiliate.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#888',
                    cursor: 'pointer',
                    padding: '0.25rem'
                  }}
                >
                  {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                </button>
              )}

              <div style={{
                width: '48px',
                height: '48px',
                background: affiliate.tier === 'director'
                  ? 'linear-gradient(135deg, #f0a500, #d4940a)'
                  : affiliate.tier === 'recruiter'
                    ? 'linear-gradient(135deg, #9b59b6, #8e44ad)'
                    : isSubAffiliate
                      ? 'linear-gradient(135deg, #3498db, #2980b9)'
                      : 'linear-gradient(135deg, #ff6b35, #f7931e)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#fff',
                flexShrink: 0
              }}>
                {affiliate.name.charAt(0).toUpperCase()}
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <h3 style={{
                    fontSize: '1.15rem',
                    fontWeight: '700',
                    color: '#e0e0e0',
                    margin: 0
                  }}>
                    {affiliate.name}
                  </h3>
                  {(affiliate.tier === 'director') && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#f0a50020',
                      color: '#f0a500',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      SALES DIRECTOR
                    </span>
                  )}
                  {(affiliate.tier === 'recruiter') && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#9b59b620',
                      color: '#9b59b6',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      RECRUITER
                    </span>
                  )}
                  {(!affiliate.tier || affiliate.tier === 'affiliate') && !isSubAffiliate && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#88888820',
                      color: '#888',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      AFFILIATE
                    </span>
                  )}
                  {affiliate.portal_enabled && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#4ecca320',
                      color: '#4ecca3',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      PORTAL
                    </span>
                  )}
                  {isSubAffiliate && (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#3498db20',
                      color: '#3498db',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      SUB
                    </span>
                  )}
                  {/* Status badge */}
                  {affiliate.terminated_at ? (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#e74c3c20',
                      color: '#e74c3c',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      TERMINATED
                    </span>
                  ) : affiliate.active ? (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#4ecca320',
                      color: '#4ecca3',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      ACTIVE
                    </span>
                  ) : (
                    <span style={{
                      padding: '0.2rem 0.5rem',
                      background: '#88888820',
                      color: '#888',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: '600'
                    }}>
                      INACTIVE
                    </span>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '0.75rem',
                  fontSize: '0.8rem',
                  color: '#888',
                  marginTop: '0.25rem',
                  flexWrap: 'wrap'
                }}>
                  {affiliate.email && <span>{affiliate.email}</span>}
                  {affiliate.phone && <span>{affiliate.phone}</span>}
                  {affiliate.terminated_at && affiliate.access_expires_at && (
                    <span style={{ color: '#e74c3c' }}>
                      Access expires: {new Date(affiliate.access_expires_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {/* Impersonate - view as affiliate */}
              <button
                onClick={() => handleImpersonate(affiliate)}
                title="View as affiliate"
                style={{
                  padding: '0.5rem',
                  background: '#3498db20',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#3498db',
                  cursor: 'pointer'
                }}
              >
                <Eye size={16} />
              </button>
              {/* Invite to portal */}
              {affiliate.portal_enabled && affiliate.email && (
                <button
                  onClick={() => handleInviteToPortal(affiliate)}
                  disabled={invitingId === affiliate.id}
                  title="Send portal invite"
                  style={{
                    padding: '0.5rem',
                    background: '#4ecca320',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#4ecca3',
                    cursor: invitingId === affiliate.id ? 'wait' : 'pointer',
                    opacity: invitingId === affiliate.id ? 0.6 : 1
                  }}
                >
                  <Mail size={16} />
                </button>
              )}
              {affiliate.can_recruit && (
                <button
                  onClick={() => handleAddSubAffiliate(affiliate)}
                  title="Add sub-affiliate"
                  style={{
                    padding: '0.5rem',
                    background: '#9b59b620',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#9b59b6',
                    cursor: 'pointer'
                  }}
                >
                  <UserPlus size={16} />
                </button>
              )}
              <button
                onClick={() => setDetailAffiliate(affiliate)}
                title="View banking & payout details"
                style={{
                  padding: '0.5rem',
                  background: '#ff6b3520',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#ff6b35',
                  cursor: 'pointer'
                }}
              >
                <Banknote size={16} />
              </button>
              <button
                onClick={() => handleEdit(affiliate)}
                style={{
                  padding: '0.5rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <Edit2 size={16} />
              </button>
              {affiliate.terminated_at ? (
                <button
                  onClick={() => handleReinstate(affiliate)}
                  title="Reinstate affiliate"
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#4ecca320',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#4ecca3',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <RotateCcw size={14} /> Reinstate
                </button>
              ) : affiliate.active ? (
                <button
                  onClick={() => { setTerminatingAffiliate(affiliate); setGraceDays(90); }}
                  title="Terminate affiliate"
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#e74c3c20',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#e74c3c',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.35rem'
                  }}
                >
                  <AlertTriangle size={14} /> Terminate
                </button>
              ) : (
                <button
                  onClick={() => handleToggleActive(affiliate)}
                  title="Reactivate affiliate"
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#3a3a3a',
                    border: 'none',
                    borderRadius: '6px',
                    color: '#888',
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                  }}
                >
                  Inactive
                </button>
              )}
              <button
                onClick={() => handleDelete(affiliate.id)}
                style={{
                  padding: '0.5rem',
                  background: '#2a1a1a',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#e74c3c',
                  cursor: 'pointer'
                }}
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '0.75rem'
          }}>
            {/* Referral Link */}
            <div style={{
              background: '#242424',
              borderRadius: '8px',
              padding: '0.75rem',
              gridColumn: 'span 2'
            }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.35rem',
                textTransform: 'uppercase'
              }}>
                Referral Link
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <code style={{
                  flex: 1,
                  fontSize: '0.8rem',
                  color: '#4ecca3',
                  fontWeight: '600',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {BASE_URL}?ref={affiliate.code}
                </code>
                <button
                  onClick={() => copyReferralLink(affiliate.code)}
                  style={{
                    padding: '0.35rem',
                    background: '#3a3a3a',
                    border: 'none',
                    borderRadius: '4px',
                    color: copiedId === affiliate.code ? '#4ecca3' : '#888',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                >
                  {copiedId === affiliate.code ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            {/* Commission */}
            <div style={{
              background: '#242424',
              borderRadius: '8px',
              padding: '0.75rem'
            }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.35rem',
                textTransform: 'uppercase'
              }}>
                Commission
              </div>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#ff6b35'
              }}>
                {formatCommissionDisplay(affiliate)}
              </div>
              {affiliate.can_recruit && (
                <div style={{ fontSize: '0.7rem', color: '#9b59b6', marginTop: '0.25rem' }}>
                  {formatOverrideDisplay(affiliate)}
                </div>
              )}
              {affiliate.commission_duration_months && (
                <div style={{ fontSize: '0.7rem', color: '#888', marginTop: '0.25rem' }}>
                  {affiliate.commission_duration_months}-month duration
                </div>
              )}
              {((parseFloat(affiliate.close_bonus_amount) || 0) + (parseFloat(affiliate.enablement_bonus_amount) || 0)) > 0 && (
                <div style={{ fontSize: '0.7rem', color: '#10B981', marginTop: '0.25rem' }}>
                  Signing &amp; onboarding: {formatCurrency((parseFloat(affiliate.close_bonus_amount) || 0) + (parseFloat(affiliate.enablement_bonus_amount) || 0))}
                </div>
              )}
            </div>

            {/* Accounts */}
            <div style={{
              background: '#242424',
              borderRadius: '8px',
              padding: '0.75rem'
            }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.35rem',
                textTransform: 'uppercase'
              }}>
                Accounts
              </div>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#3498db'
              }}>
                {affiliate.accountCount}
              </div>
            </div>

            {/* Earnings */}
            <div style={{
              background: '#242424',
              borderRadius: '8px',
              padding: '0.75rem'
            }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.35rem',
                textTransform: 'uppercase'
              }}>
                Earned
              </div>
              <div style={{
                fontSize: '1.1rem',
                fontWeight: '700',
                color: '#4ecca3'
              }}>
                {formatCurrency(affiliate.totalEarned)}
              </div>
              {affiliate.totalOwed > 0 && (
                <div style={{ fontSize: '0.7rem', color: '#f39c12', marginTop: '0.25rem' }}>
                  {formatCurrency(affiliate.totalOwed)} owed
                </div>
              )}
            </div>

            {/* Team Size (for recruiters) */}
            {affiliate.can_recruit && (
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '0.75rem'
              }}>
                <div style={{
                  fontSize: '0.7rem',
                  color: '#666',
                  marginBottom: '0.35rem',
                  textTransform: 'uppercase'
                }}>
                  Team Size
                </div>
                <div style={{
                  fontSize: '1.1rem',
                  fontWeight: '700',
                  color: '#9b59b6'
                }}>
                  {affiliate.subCount}
                  <span style={{ fontSize: '0.75rem', color: '#888', fontWeight: '400' }}> subs</span>
                </div>
              </div>
            )}
          </div>

          {affiliate.notes && (
            <div style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: '#242424',
              borderRadius: '6px',
              fontSize: '0.8rem',
              color: '#888'
            }}>
              {affiliate.notes}
            </div>
          )}

          {/* Direct Deposit Info */}
          <div style={{
            marginTop: '0.75rem',
            padding: '0.75rem',
            background: '#242424',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <Banknote size={18} color={affiliate.payout_setup_complete ? '#4ecca3' : '#666'} />
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.2rem',
                textTransform: 'uppercase'
              }}>
                Direct Deposit Info
              </div>
              {affiliate.payout_setup_complete ? (
                <div style={{ fontSize: '0.85rem', color: '#e0e0e0' }}>
                  {affiliate.bank_name} • {affiliate.account_type} ••••{affiliate.account_number_last4 || affiliate.account_number?.slice(-4)}
                  {affiliate.tax_id_last4 && (
                    <span style={{ color: '#888', marginLeft: '0.5rem' }}>
                      • {affiliate.tax_id_type?.toUpperCase()} ••••{affiliate.tax_id_last4}
                    </span>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#f39c12' }}>
                  Not set up yet
                </div>
              )}
            </div>
          </div>

          {/* W-9 Status */}
          <div style={{
            marginTop: '0.5rem',
            padding: '0.75rem',
            background: '#242424',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <FileText size={18} color={affiliate.w9_uploaded_at ? '#4ecca3' : '#e74c3c'} />
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '0.7rem',
                color: '#666',
                marginBottom: '0.2rem',
                textTransform: 'uppercase'
              }}>
                W-9 Tax Form
              </div>
              {affiliate.w9_uploaded_at ? (
                <div style={{ fontSize: '0.85rem', color: '#4ecca3' }}>
                  Submitted {new Date(affiliate.w9_uploaded_at).toLocaleDateString()}
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: '#e74c3c' }}>
                  Not submitted
                </div>
              )}
            </div>
            {affiliate.w9_file_path && (
              <button
                onClick={async () => {
                  const { data } = await supabase.storage
                    .from('affiliate-documents')
                    .createSignedUrl(affiliate.w9_file_path, 3600);
                  if (data?.signedUrl) {
                    window.open(data.signedUrl, '_blank');
                  }
                }}
                title="Download W-9"
                style={{
                  padding: '0.4rem',
                  background: '#3a3a3a',
                  border: 'none',
                  borderRadius: '6px',
                  color: '#4ecca3',
                  cursor: 'pointer'
                }}
              >
                <Download size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Sub-affiliates */}
        {affiliate.can_recruit && isExpanded && subAffiliates.length > 0 && (
          <div style={{ marginTop: '0.5rem' }}>
            {subAffiliates.map(sub => (
              <AffiliateCard key={sub.id} affiliate={sub} isSubAffiliate />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem'
          }}>
            Affiliates
          </h1>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            Manage affiliate salespeople, teams, and commission structures
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setEditingId(null);
            setShowForm(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Add Affiliate
        </button>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #ff6b35'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Total Affiliates
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff6b35' }}>
            {affiliates.length}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #9b59b6'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Recruiters
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#9b59b6' }}>
            {affiliates.filter(a => a.can_recruit).length}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #3498db'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Total Accounts
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3498db' }}>
            {affiliates.reduce((sum, a) => sum + a.accountCount, 0)}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #f39c12'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Total Owed
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f39c12' }}>
            {formatCurrency(affiliates.reduce((sum, a) => sum + a.totalOwed, 0))}
          </div>
        </div>
      </div>

      {/* Affiliate List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {affiliates.length === 0 ? (
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '3rem',
            textAlign: 'center',
            color: '#888'
          }}>
            <Users size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>No affiliates yet. Add your first affiliate to get started.</p>
          </div>
        ) : (
          topLevelAffiliates.map(affiliate => (
            <AffiliateCard key={affiliate.id} affiliate={affiliate} />
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '1.5rem',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e0e0e0' }}>
                {editingId ? 'Edit Affiliate' : form.parent_affiliate_id ? 'Add Sub-Affiliate' : 'Add Affiliate'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Users size={16} /> Basic Info
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Referral Code *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.code}
                      onChange={(e) => setForm({ ...form, code: e.target.value })}
                      placeholder="e.g., mike, sales1"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Phone
                    </label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Role & Hierarchy */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Shield size={16} /> Role & Hierarchy
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Tier
                    </label>
                    <select
                      value={form.tier}
                      onChange={(e) => {
                        const tier = e.target.value;
                        // Director default per current comp plan: 20% perpetual, $50 bonus.
                        // Only auto-apply when switching INTO director from a non-director tier so
                        // we don't clobber values an admin has manually edited mid-form.
                        const becomingDirector = tier === 'director' && form.tier !== 'director';
                        setForm({
                          ...form,
                          tier,
                          can_recruit: tier === 'recruiter' || tier === 'director',
                          ...(becomingDirector ? {
                            commission_model: 'percentage',
                            commission_rate: '20',
                            override_rate: '20',
                            close_bonus_amount: '50',
                            enablement_bonus_amount: '0',
                          } : {}),
                        });
                      }}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="affiliate">Affiliate</option>
                      <option value="recruiter">Recruiter (manages affiliates)</option>
                      <option value="director">Sales Director (manages recruiters)</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Reports To
                    </label>
                    <select
                      value={form.parent_affiliate_id}
                      onChange={(e) => setForm({ ...form, parent_affiliate_id: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="">None (reports directly to Work Van)</option>
                      {availableParents.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.name} ({p.code}) — {p.tier || 'affiliate'}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {form.tier !== 'affiliate' && (
                  <div style={{
                    background: form.tier === 'director' ? '#f0a50010' : '#9b59b610',
                    border: `1px solid ${form.tier === 'director' ? '#f0a50030' : '#9b59b630'}`,
                    borderRadius: '6px',
                    padding: '0.6rem 0.75rem',
                    marginBottom: '0.75rem',
                    fontSize: '0.8rem',
                    color: '#aaa'
                  }}>
                    {form.tier === 'director'
                      ? 'Directors manage recruiters who manage affiliates. They set the commission split for their downline.'
                      : 'Recruiters manage affiliates and can split their commission pool with their team.'}
                  </div>
                )}

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#e0e0e0',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}>
                  <input
                    type="checkbox"
                    checked={form.portal_enabled}
                    onChange={(e) => setForm({ ...form, portal_enabled: e.target.checked })}
                    style={{ width: '18px', height: '18px', accentColor: '#4ecca3' }}
                  />
                  Portal Access
                </label>
              </div>

              {/* Recurring Commission */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <DollarSign size={16} /> Recurring Commission
                </h3>
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.75rem' }}>
                  {form.parent_affiliate_id
                    ? 'Their share of revenue per referred account. This comes out of their manager\'s pool.'
                    : 'The total % of account revenue paid out for each referral this person brings in.'}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Type
                    </label>
                    <select
                      value={form.commission_model}
                      onChange={(e) => setForm({ ...form, commission_model: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      <option value="fixed">Fixed $ per month</option>
                      <option value="percentage">% of revenue</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      {form.commission_model === 'fixed' ? 'Amount ($)' : 'Rate (%)'}
                    </label>
                    <div style={{ position: 'relative' }}>
                    <input
                      type="number"
                      step={form.commission_model === 'fixed' ? '0.01' : '1'}
                      min="0"
                      max={form.commission_model === 'percentage' ? '100' : undefined}
                      required
                      value={form.commission_rate}
                      onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                      placeholder={form.commission_model === 'percentage' ? 'e.g. 25' : 'e.g. 5.00'}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        paddingRight: form.commission_model === 'percentage' ? '2rem' : '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                    {form.commission_model === 'percentage' && (
                      <span style={{
                        position: 'absolute',
                        right: '0.6rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#888',
                        fontSize: '0.9rem',
                        pointerEvents: 'none'
                      }}>%</span>
                    )}
                    </div>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                    Payout Duration
                  </label>
                  <select
                    value={['', '12', '15', '24'].includes(form.commission_duration_months) ? form.commission_duration_months : 'custom'}
                    onChange={(e) => setForm({ ...form, commission_duration_months: e.target.value === 'custom' ? '6' : e.target.value })}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: '#1a1a1a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#e0e0e0',
                      fontSize: '0.9rem'
                    }}
                  >
                    <option value="">Perpetual (pays as long as account is active)</option>
                    <option value="12">12 months per account</option>
                    <option value="15">15 months per account</option>
                    <option value="24">24 months per account</option>
                    <option value="custom">Custom...</option>
                  </select>
                  {form.commission_duration_months && !['12', '15', '24', ''].includes(form.commission_duration_months) && (
                    <input
                      type="number"
                      min="1"
                      value={form.commission_duration_months}
                      onChange={(e) => setForm({ ...form, commission_duration_months: e.target.value })}
                      placeholder="Number of months"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem',
                        marginTop: '0.5rem'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Team Management — only for recruiters/directors */}
              {(form.tier === 'recruiter' || form.tier === 'director') && (
                <div style={{
                  background: '#242424',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  borderLeft: `3px solid ${form.tier === 'director' ? '#f0a500' : '#9b59b6'}`
                }}>
                  <h3 style={{
                    color: form.tier === 'director' ? '#f0a500' : '#9b59b6',
                    fontSize: '0.9rem',
                    marginBottom: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Users size={16} /> Team Management
                  </h3>
                  <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.75rem' }}>
                    {form.tier === 'director'
                      ? 'The recurring commission rate above is this director\'s total payout allotment. They control how it gets split to their managers and affiliates.'
                      : 'This person can add affiliates under them. Their override is what they keep from their team\'s sales.'}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem', opacity: form.can_set_sub_rates ? 0.4 : 1 }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                        Override Type
                      </label>
                      <select
                        value={form.override_model}
                        onChange={(e) => setForm({ ...form, override_model: e.target.value })}
                        disabled={form.can_set_sub_rates}
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: '#1a1a1a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          color: '#e0e0e0',
                          fontSize: '0.9rem',
                          cursor: form.can_set_sub_rates ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <option value="fixed">% of each sale's revenue</option>
                        <option value="percentage_of_sub">% of each sub's commission</option>
                        <option value="from_pool">Shared pool (split among team)</option>
                      </select>
                    </div>

                    {form.override_model === 'from_pool' ? (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                          Pool Size (% of revenue)
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            value={form.pool_rate}
                            onChange={(e) => setForm({ ...form, pool_rate: e.target.value })}
                            placeholder="e.g. 20"
                            disabled={form.can_set_sub_rates}
                            style={{
                              width: '100%',
                              padding: '0.6rem',
                              paddingRight: '2rem',
                              background: '#1a1a1a',
                              border: '1px solid #3a3a3a',
                              borderRadius: '6px',
                              color: '#e0e0e0',
                              fontSize: '0.9rem',
                              cursor: form.can_set_sub_rates ? 'not-allowed' : 'text'
                            }}
                          />
                          <span style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none' }}>%</span>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                          Override Rate (%)
                        </label>
                        <div style={{ position: 'relative' }}>
                          <input
                            type="number"
                            step="1"
                            min="0"
                            max="100"
                            value={form.override_rate}
                            onChange={(e) => setForm({ ...form, override_rate: e.target.value })}
                            placeholder="e.g. 5"
                            disabled={form.can_set_sub_rates}
                            style={{
                              width: '100%',
                              padding: '0.6rem',
                              paddingRight: '2rem',
                              background: '#1a1a1a',
                              border: '1px solid #3a3a3a',
                              borderRadius: '6px',
                              color: '#e0e0e0',
                              fontSize: '0.9rem',
                              cursor: form.can_set_sub_rates ? 'not-allowed' : 'text'
                            }}
                          />
                          <span style={{ position: 'absolute', right: '0.6rem', top: '50%', transform: 'translateY(-50%)', color: '#888', pointerEvents: 'none' }}>%</span>
                        </div>
                      </div>
                    )}
                  </div>
                  {form.can_set_sub_rates && (
                    <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '-0.25rem', marginBottom: '0.75rem', fontStyle: 'italic' }}>
                      Override controlled by this {form.tier === 'director' ? 'director' : 'recruiter'} — they set their team's rates from their own commission allotment.
                    </div>
                  )}

                  {form.tier === 'director' && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', marginBottom: '0.35rem', color: '#f0a500', fontSize: '0.85rem' }}>
                        Max Override Budget ($/mo)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.director_max_override}
                        onChange={(e) => setForm({ ...form, director_max_override: e.target.value })}
                        placeholder="0 = unlimited"
                        disabled={form.can_set_sub_rates}
                        style={{
                          width: '100%',
                          padding: '0.6rem',
                          background: '#1a1a1a',
                          border: '1px solid #3a3a3a',
                          borderRadius: '6px',
                          color: '#e0e0e0',
                          fontSize: '0.9rem',
                          opacity: form.can_set_sub_rates ? 0.4 : 1,
                          cursor: form.can_set_sub_rates ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>
                  )}

                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#e0e0e0',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}>
                    <input
                      type="checkbox"
                      checked={form.can_set_sub_rates}
                      onChange={(e) => setForm({ ...form, can_set_sub_rates: e.target.checked })}
                      style={{ width: '16px', height: '16px', accentColor: form.tier === 'director' ? '#f0a500' : '#9b59b6' }}
                    />
                    Allow them to set commission rates for their team
                  </label>
                </div>
              )}

              {/* Bonuses */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Banknote size={16} /> Signing & Onboarding Bonus
                </h3>
                <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.75rem' }}>
                  One-time payment when the referred account makes their first
                  subscription payment.
                </div>

                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                    Bonus ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={(parseFloat(form.close_bonus_amount) || 0) + (parseFloat(form.enablement_bonus_amount) || 0) || ''}
                    onChange={(e) => setForm({ ...form, close_bonus_amount: e.target.value, enablement_bonus_amount: '0' })}
                    placeholder="50"
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: '#1a1a1a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#e0e0e0',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                <div style={{ display: form.tier === 'director' ? 'block' : 'none' }}>
                  {form.tier === 'director' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={form.can_grant_deal_bonus}
                        onChange={(e) => setForm({ ...form, can_grant_deal_bonus: e.target.checked })}
                        style={{ accentColor: '#10B981' }}
                      />
                      <label style={{ color: '#aaa', fontSize: '0.85rem' }}>
                        Can set bonus amounts for their team
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                  Notes
                </label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: '#242424',
                    border: '1px solid #3a3a3a',
                    borderRadius: '6px',
                    color: '#e0e0e0',
                    fontSize: '0.9rem',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: '#ff6b35',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingId ? 'Save Changes' : 'Add Affiliate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Termination Confirmation Modal */}
      {terminatingAffiliate && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #e74c3c40',
            padding: '1.5rem',
            maxWidth: '480px',
            width: '100%'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              marginBottom: '1.25rem'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: '#e74c3c20',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <AlertTriangle size={20} color="#e74c3c" />
              </div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#e0e0e0', margin: 0 }}>
                Terminate Affiliate
              </h2>
            </div>

            <p style={{ color: '#ccc', fontSize: '0.9rem', marginBottom: '1.25rem', lineHeight: 1.5 }}>
              Terminate <strong style={{ color: '#fff' }}>{terminatingAffiliate.name}</strong>? They will have read-only portal access for <strong style={{ color: '#fff' }}>{graceDays}</strong> days.
            </p>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                Grace Period (days)
              </label>
              <input
                type="number"
                min="0"
                max="365"
                value={graceDays}
                onChange={(e) => setGraceDays(parseInt(e.target.value) || 0)}
                style={{
                  width: '120px',
                  padding: '0.6rem',
                  background: '#242424',
                  border: '1px solid #3a3a3a',
                  borderRadius: '6px',
                  color: '#e0e0e0',
                  fontSize: '0.9rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => { setTerminatingAffiliate(null); setGraceDays(90); }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#2a2a2a',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#e0e0e0',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleTerminate(terminatingAffiliate)}
                style={{
                  flex: 2,
                  padding: '0.75rem',
                  background: '#e74c3c',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Confirm Termination
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Affiliate Detail Modal */}
      {detailAffiliate && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '2rem'
        }}
          onClick={() => setDetailAffiliate(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#1a1a1a',
              borderRadius: '12px',
              border: '1px solid #2a2a2a',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: '#e0e0e0', fontSize: '1.25rem', fontWeight: '700', margin: 0 }}>
                {detailAffiliate.name} — Details
              </h2>
              <button
                onClick={() => setDetailAffiliate(null)}
                style={{ background: 'transparent', border: 'none', color: '#888', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Contact Info */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: '600' }}>Contact</div>
              <div style={{ background: '#242424', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Email</span>
                  <span style={{ color: '#e0e0e0' }}>{detailAffiliate.email || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Phone</span>
                  <span style={{ color: '#e0e0e0' }}>{detailAffiliate.phone || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Code</span>
                  <span style={{ color: '#ff6b35', fontFamily: 'monospace' }}>{detailAffiliate.code}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Commission</span>
                  <span style={{ color: '#4ecca3' }}>{formatCommissionDisplay(detailAffiliate)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Status</span>
                  <span style={{ color: detailAffiliate.active ? '#4ecca3' : '#e74c3c' }}>
                    {detailAffiliate.terminated_at ? 'Terminated' : detailAffiliate.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Banking Info */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Banknote size={14} />
                Direct Deposit / Banking
              </div>
              <div style={{ background: '#242424', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Payout Setup</span>
                  <span style={{ color: detailAffiliate.payout_setup_complete ? '#4ecca3' : detailAffiliate.payout_setup_skipped ? '#f39c12' : '#e74c3c' }}>
                    {detailAffiliate.payout_setup_complete ? 'Complete' : detailAffiliate.payout_setup_skipped ? 'Skipped' : 'Not Set Up'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Bank Name</span>
                  <span style={{ color: '#e0e0e0' }}>{detailAffiliate.bank_name || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Account Type</span>
                  <span style={{ color: '#e0e0e0', textTransform: 'capitalize' }}>{detailAffiliate.account_type || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Account Holder</span>
                  <span style={{ color: '#e0e0e0' }}>{detailAffiliate.account_holder_name || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Routing (last 4)</span>
                  <span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>
                    {detailAffiliate.routing_number_last4 ? `••••${detailAffiliate.routing_number_last4}` : '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Account (last 4)</span>
                  <span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>
                    {detailAffiliate.account_number_last4 ? `••••${detailAffiliate.account_number_last4}` : '-'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tax Info */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FileText size={14} />
                Tax Information
              </div>
              <div style={{ background: '#242424', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Tax ID Type</span>
                  <span style={{ color: '#e0e0e0', textTransform: 'uppercase' }}>{detailAffiliate.tax_id_type || '-'}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Tax ID (last 4)</span>
                  <span style={{ color: '#e0e0e0', fontFamily: 'monospace' }}>
                    {detailAffiliate.tax_id_last4 ? `••••${detailAffiliate.tax_id_last4}` : '-'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>W-9 Status</span>
                  <span style={{ color: detailAffiliate.w9_uploaded_at ? '#4ecca3' : '#e74c3c' }}>
                    {detailAffiliate.w9_uploaded_at
                      ? `Submitted ${new Date(detailAffiliate.w9_uploaded_at).toLocaleDateString()}`
                      : 'Not submitted'}
                  </span>
                </div>
                {detailAffiliate.w9_file_path && (
                  <button
                    onClick={async () => {
                      const { data } = await supabase.storage
                        .from('affiliate-documents')
                        .createSignedUrl(detailAffiliate.w9_file_path, 3600);
                      if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                    }}
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem 1rem',
                      background: '#2a2a2a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#4ecca3',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.85rem'
                    }}
                  >
                    <Download size={14} />
                    Download W-9
                  </button>
                )}
              </div>
            </div>

            {/* Portal Info */}
            <div>
              <div style={{ fontSize: '0.75rem', color: '#888', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Shield size={14} />
                Portal Access
              </div>
              <div style={{ background: '#242424', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Tier</span>
                  <span style={{
                    color: detailAffiliate.tier === 'director' ? '#f0a500' : detailAffiliate.tier === 'recruiter' ? '#9b59b6' : '#888',
                    fontWeight: '600',
                    textTransform: 'uppercase'
                  }}>
                    {detailAffiliate.tier === 'director' ? 'Sales Director' : detailAffiliate.tier || 'Affiliate'}
                  </span>
                </div>
                {detailAffiliate.tier === 'director' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#888' }}>Max Override Budget</span>
                    <span style={{ color: '#f0a500', fontFamily: 'monospace' }}>{formatCurrency(detailAffiliate.director_max_override || 0)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Portal Enabled</span>
                  <span style={{ color: detailAffiliate.portal_enabled ? '#4ecca3' : '#888' }}>
                    {detailAffiliate.portal_enabled ? 'Yes' : 'No'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Can Recruit</span>
                  <span style={{ color: detailAffiliate.can_recruit ? '#9b59b6' : '#888' }}>
                    {detailAffiliate.can_recruit ? 'Yes' : 'No'}
                  </span>
                </div>
                {detailAffiliate.terminated_at && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#888' }}>Terminated</span>
                      <span style={{ color: '#e74c3c' }}>{new Date(detailAffiliate.terminated_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#888' }}>Access Expires</span>
                      <span style={{ color: '#f39c12' }}>{detailAffiliate.access_expires_at ? new Date(detailAffiliate.access_expires_at).toLocaleDateString() : '-'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
